import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FaUsers,
  FaCalendarAlt,
  FaLayerGroup,
  FaFileAlt,
} from "react-icons/fa";
import { superAdminDashboardActions } from "../../../_store/superAdminDashboard.slice";
import { committeeAdminDashboardActions } from "../../../_store/committeeAdminDashboard.slice";
import Dashboard from "../../../components/common/ui/Charts/Dashboard";
import { SelectInput } from "../../../components/common/ui/Inputs";
import { userRegisterAction } from "../../../_store";
import { useMemo } from "react";
import MonthYearPicker from "../../../components/common/ui/Inputs/MonthYearPicker";
import { format, subYears } from "date-fns";
import { exportExcel } from "../../../utils/exportExcel";
import { FaFileExcel } from "react-icons/fa";
import Button from "../../../components/common/ui/Button";
import { masterActions } from "../../../_store/master.slice";
import CustomLoader from "../../../components/common/ui/Loader";

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});

  const {
    cardCountsVisibleToSuperAdmin,
    loginStatusVisibleToSuperAdmin,
    membersCountVisibleToSuperAdmin,
    eventsCountVisibleToSuperAdmin,
    committeeMembershipVisibleToSuperAdmin,
    loading,
  } = useSelector((state) => state.superAdminDashboard);

  const {
    cardCountsVisibleToCommitteeAdmin,
    loginStatusVisibleToCommitteeAdmin,
    monthlyEventsCountVisibleToCommitteeAdmin,
    yearlyEventsCountVisibleToCommitteeAdmin,
  } = useSelector((state) => state.committeeAdminDashboard);

  const { committeesList } = useSelector((state) => state.register);
  const { getCommitteeListResponse } = useSelector((state) => state.master);
  const userRoleID = parseInt(sessionStorage.getItem("userRoleID"), 10);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [eventView, setEventView] = useState("monthly");
  const [filters, setFilters] = useState({
    fromDate: format(new Date(2014, 0), "yyyy-MM"),
    toDate: format(new Date(), "yyyy-MM"), // current month
  });

  // Prepare dropdown options
  const committeeOptions = React.useMemo(() => {
    if (!getCommitteeListResponse?.data) return [];

    // If super admin (userRoleID === 3) → add "All"
    if (userRoleID === 3) {
      return [{ label: "All", value: "all" }, ...getCommitteeListResponse.data];
    }
    return getCommitteeListResponse.data;
  }, [getCommitteeListResponse, userRoleID]);

  useEffect(() => {
    if (!committeeOptions?.length) return; // wait until options are ready

    if (userRoleID === 3) {
      setSelectedCommittee("all");
    } else if (userRoleID === 2) {
      setSelectedCommittee(committeeOptions[0].value);
    }
  }, [committeeOptions, userRoleID]);

  useEffect(() => {
    dispatch(masterActions?.getCommitteeList());
  }, [dispatch]);

  // 🔹 1. Main fetch function (for initial load + search)
  const fetchDashboardData = () => {
    if (userRoleID === 3 && selectedCommittee === "all") {
      dispatch(
        superAdminDashboardActions.getloginStatusVisibleToSuperAdmin({
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
      dispatch(
        superAdminDashboardActions.getSuperAdminDashboardCardCount({
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
      dispatch(
        superAdminDashboardActions.getMembersCountbyEachCommittee({
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
      dispatch(
        superAdminDashboardActions.getEventCountByCommittee({
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
      dispatch(
        superAdminDashboardActions.getCommitteeMembershipPercentage({
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
    } else if (selectedCommittee) {
      dispatch(
        committeeAdminDashboardActions.getCommitteeAdminDashboardCardCount({
          id: selectedCommittee,
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
      dispatch(
        committeeAdminDashboardActions.getloginStatusVisibleToCommitteeAdmin({
          id: selectedCommittee,
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );

      // 👇 default to monthly view initially
      dispatch(
        committeeAdminDashboardActions.getMonthlyEventCountByCommitteeAdmin({
          id: selectedCommittee,
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
    }
  };

  // 🔹 2. Initial load (runs once)
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommittee]);

  // 🔹 3. Search button
  const handleSearch = () => {
    fetchDashboardData();
  };

  // 🔹 4. Graph toggle (eventView) → always auto refresh
  useEffect(() => {
    if (!selectedCommittee || selectedCommittee === "all") return;

    if (eventView === "monthly") {
      dispatch(
        committeeAdminDashboardActions.getMonthlyEventCountByCommitteeAdmin({
          id: selectedCommittee,
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
    } else if (eventView === "yearly") {
      dispatch(
        committeeAdminDashboardActions.getYearlyEventCountByCommitteeAdmin({
          id: selectedCommittee,
          startDate: filters?.fromDate,
          endDate: filters?.toDate,
        }),
      );
    }
  }, [eventView, selectedCommittee, filters, dispatch]);

  const handleSelectChange = (selectedOption, fieldName) => {
    debugger;
    if (!selectedOption) {
      // setSelectedCommittee(null);
      setSelectedCommittee("all");
      return;
    }
    setSelectedCommittee(selectedOption.value); // store only value
  };

  const onExport = (type, from, to) => {
    const { fromDate, toDate } = filters;
    switch (type) {
      case "SuperAdminLoginStatus":
        dispatch(
          superAdminDashboardActions.exportSuperAdminLoginStatus({
            startDate: fromDate,
            endDate: toDate,
          }),
        )
          .unwrap()
          .then((result) => {
            const formattedData = result.map((item) => ({
              Month: item.month,
              Year: item.year,
              Committee: item.committeeName,
              "Logged In User List": item.logedInUsersList.join(", "), // optional
              "Logged In Users Count": item.logedInUsersCount,
              "Not Logged In User List": item.notLogedInUsersList.join(", "), // optional
              "Not Logged In Users Count": item.notLogedInUsersCount,
            }));
            exportExcel(
              formattedData,
              "User-Login-report.xlsx",
              "Login_Status",
            );
          })
          .catch((err) => {
            console.error("Export failed:", err);
          });
        break;
      case "CommitteeAdminLoginStatus":
        dispatch(
          committeeAdminDashboardActions.exportCommitteeLoginStatus({
            committee: selectedCommittee,
            startDate: fromDate,
            endDate: toDate,
          }),
        )
          .unwrap()
          .then((result) => {
            const formattedData = result.map((item) => ({
              Month: item.month,
              Year: item.year,
              Committee: item.committeeName,
              "Logged In User List": item.logedInUsersList.join(", "), // optional
              "Logged In Users Count": item.logedInUsersCount,
              "Not Logged In User List": item.notLogedInUsersList.join(", "), // optional
              "Not Logged In Users Count": item.notLogedInUsersCount,
            }));
            exportExcel(
              formattedData,
              "User-Login-report.xlsx",
              "Login_Status",
            );
          })
          .catch((err) => {
            console.error("Export failed:", err);
          });
        break;
      case "CommitteeAdminEvents":
        dispatch(
          committeeAdminDashboardActions.exportCommitteeAdminEventData({
            committee: selectedCommittee,
            startDate: fromDate,
            endDate: toDate,
          }),
        )
          .unwrap()
          .then((result) => {
            const formattedData = result.map((item) => ({
              Month: item.month,
              Year: item.year,
              "Total Events": item.eventCount,
            }));

            exportExcel(formattedData, "committee-Event-report.xlsx", "Events");
          })
          .catch((err) => {
            console.error("Export failed:", err);
          });
        break;
      case "SuperAdminEvents":
        dispatch(
          superAdminDashboardActions.exportSuperAdminEventData({
            startDate: fromDate,
            endDate: toDate,
          }),
        )
          .unwrap()
          .then((result) => {
            const formattedData = result.map((item) => ({
              Month: item.month,
              Year: item.year,
              "Committee Name": item?.committeeName,
              "Total Events": item?.eventCount,
            }));

            exportExcel(
              formattedData,
              "SuperAdmin-Event-report.xlsx",
              "Events",
            );
          })
          .catch((err) => {
            console.error("Export failed:", err);
          });
        break;
      case "SuperAdminMembers":
        dispatch(
          superAdminDashboardActions.exportSuperAdminMembersCountData({
            startDate: fromDate,
            endDate: toDate,
          }),
        )
          .unwrap()
          .then((result) => {
            const formattedData = result.map((item) => ({
              Month: item.month,
              Year: item.year,
              "Committee Name": item?.committeeName,
              "Members Count": item?.userCount,
            }));

            exportExcel(
              formattedData,
              "SuperAdmin-Members-report.xlsx",
              "Members",
            );
          })
          .catch((err) => {
            console.error("Export failed:", err);
          });
        break;
      default:
        console.error("Export failed:");
    }
  };

  // ✅ Stats
  const stats = useMemo(
    () => [
      {
        title: "Active Users",
        info: <i class="fa fa-info-circle info-icon" aria-hidden="true"></i>,
        // infoTitle:
        //   "Displays Active Members count, including Committee Chair and Super Admin",
        infoTitle: `${selectedCommittee === "all" ? "Displays Active Members count, including Committee Chair and Super Admin" : "Displays Active Members count, including Committee Chair"}`,
        value:
          selectedCommittee === "all"
            ? cardCountsVisibleToSuperAdmin?.data?.users
            : cardCountsVisibleToCommitteeAdmin?.data?.users,
        icon: <FaUsers />,
        color: "primary",
        //  change: 5.2,
      },
      {
        title: "Events",
        info: <i class="fa fa-info-circle info-icon" aria-hidden="true"></i>,
        infoTitle: "Displays events count based on their scheduled date",
        value:
          selectedCommittee === "all"
            ? cardCountsVisibleToSuperAdmin?.data?.events
            : cardCountsVisibleToCommitteeAdmin?.data?.events,
        icon: <FaCalendarAlt />,
        color: "success",
        // change: -2.5,
      },
      {
        title: "Categories",
        value:
          selectedCommittee === "all"
            ? cardCountsVisibleToSuperAdmin?.data?.categorys
            : cardCountsVisibleToCommitteeAdmin?.data?.categorys,
        icon: <FaLayerGroup />,
        color: "warning",
        //  change: 1.8,
      },
      {
        title: "Documents",
        value:
          selectedCommittee === "all"
            ? cardCountsVisibleToSuperAdmin?.data?.documents
            : cardCountsVisibleToCommitteeAdmin?.data?.documents,
        icon: <FaFileAlt />,
        color: "danger",
        //  change: 0.5,
      },
    ],
    [
      selectedCommittee,
      cardCountsVisibleToSuperAdmin,
      cardCountsVisibleToCommitteeAdmin,
    ],
  );

  const safeArray = (arr) => (Array.isArray(arr) ? arr.filter((x) => x) : []);

  const loginSeries = [
    Number(loginStatusVisibleToSuperAdmin?.data?.loggedInPercentage) || 0,
    Number(loginStatusVisibleToSuperAdmin?.data?.notLoggedInPercentage) || 0,
  ];
  const allCharts = useMemo(
    () => [
      {
        type: "pie",
        // title: "User Login Status",
        headerContent: (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span>User Login Status</span>
            </div>
            {loginSeries.some((val) => val > 0) && (
              <button
                className="btn btn-success btn-sm d-flex align-items-center gap-1"
                onClick={() =>
                  onExport(
                    "SuperAdminLoginStatus",
                    filters?.fromDate,
                    filters?.toDate,
                  )
                }
              >
                <FaFileExcel size={14} />
                <span>Export to Excel</span>
              </button>
            )}
          </div>
        ),
        labels: ["Logged In", "Not Logged In"],
        series: loginSeries,
        // series: [
        //   Number(loginStatusVisibleToSuperAdmin?.data?.loggedInPercentage) || 0,
        //   Number(loginStatusVisibleToSuperAdmin?.data?.notLoggedInPercentage) ||
        //     0,
        // ],
        colors: ["#008FFB", "#FEB019"],
        exportEnabled: true,
      },
      {
        type: "bar",
        // title: "Users per Committee",
        headerContent: (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span>User per Committee</span>
            </div>
            {safeArray(membersCountVisibleToSuperAdmin).length > 0 && (
              <button
                className="btn btn-success btn-sm d-flex align-items-center gap-1"
                onClick={() =>
                  onExport(
                    "SuperAdminMembers",
                    filters?.fromDate,
                    filters?.toDate,
                  )
                }
              >
                <FaFileExcel size={14} />
                <span>Export to Excel</span>
              </button>
            )}
          </div>
        ),
        labels:
          safeArray(membersCountVisibleToSuperAdmin).length > 0
            ? safeArray(membersCountVisibleToSuperAdmin).map((x) =>
                String(x?.committeeName ?? "Unknown"),
              )
            : ["No Data"],
        series: [
          {
            name: "Users",
            data:
              safeArray(membersCountVisibleToSuperAdmin).length > 0
                ? safeArray(membersCountVisibleToSuperAdmin).map(
                    (x) => Number(x?.userCount) || 0,
                  )
                : [0],
          },
        ],
        colors: ["#007bff"],
        exportEnabled: true,
      },

      {
        type: "bar",
        headerContent: (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span>Events per Committee</span>
            </div>
            {safeArray(eventsCountVisibleToSuperAdmin).length > 0 && (
              <button
                className="btn btn-success btn-sm d-flex align-items-center gap-1"
                onClick={() =>
                  onExport(
                    "SuperAdminEvents",
                    filters?.fromDate,
                    filters?.toDate,
                  )
                }
              >
                <FaFileExcel size={14} />
                <span>Export to Excel</span>
              </button>
            )}
          </div>
        ),
        labels:
          safeArray(eventsCountVisibleToSuperAdmin).length > 0
            ? safeArray(eventsCountVisibleToSuperAdmin).map((x) =>
                String(x?.committeeName ?? "Unknown"),
              )
            : ["No Data"],
        series: [
          {
            name: "Events",
            data:
              safeArray(eventsCountVisibleToSuperAdmin).length > 0
                ? safeArray(eventsCountVisibleToSuperAdmin).map(
                    (x) => Number(x?.eventCount) || 0,
                  )
                : [0],
          },
        ],
        colors: ["#00E396"],
        exportEnabled: false,
      },

      {
        type: "pie",
        headerContent: (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span>Committee Membership Percentage</span>
            </div>
          </div>
        ),

        labels:
          safeArray(committeeMembershipVisibleToSuperAdmin?.data).length > 0
            ? committeeMembershipVisibleToSuperAdmin.data.map(
                (x) => x.Category.charAt(0).toUpperCase() + x.Category.slice(1),
              )
            : ["No Data"],
        series:
          safeArray(committeeMembershipVisibleToSuperAdmin?.data).length > 0
            ? committeeMembershipVisibleToSuperAdmin.data.map(
                (x) => Number(x.Percentage) || 0,
              )
            : [0],
        colors: ["#008FFB", "#FEB019", "#FF4560", "#775DD0"],
        exportEnabled: false,
      },
    ],
    [
      loginStatusVisibleToSuperAdmin,
      membersCountVisibleToSuperAdmin,
      eventsCountVisibleToSuperAdmin,
      committeeMembershipVisibleToSuperAdmin,
    ],
  );

  const singleCommitteeCharts = useMemo(
    () => [
      {
        type: "pie",
        // title: "User Login Status",
        headerContent: (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span>User Login Status</span>
            </div>
            {(Number(
              loginStatusVisibleToCommitteeAdmin?.data?.loggedInPercentage,
            ) > 0 ||
              Number(
                loginStatusVisibleToCommitteeAdmin?.data?.notLoggedInPercentage,
              ) > 0) && (
              <button
                className="btn btn-success btn-sm d-flex align-items-center gap-1"
                onClick={() =>
                  onExport(
                    "CommitteeAdminLoginStatus",
                    filters?.fromDate,
                    filters?.toDate,
                  )
                }
              >
                <FaFileExcel size={14} />
                <span>Export to Excel</span>
              </button>
            )}
          </div>
        ),
        labels: ["Logged In", "Not Logged In"],
        series: [
          Number(
            loginStatusVisibleToCommitteeAdmin?.data?.loggedInPercentage,
          ) || 0,
          Number(
            loginStatusVisibleToCommitteeAdmin?.data?.notLoggedInPercentage,
          ) || 0,
        ],
        colors: ["#008FFB", "#FEB019"],
        exportEnabled: true,
      },
      {
        type: "bar",
        headerContent: (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span>Events per Committee ({eventView})</span>
            </div>
            <div className="btn-group btn-group-sm me-3" role="group">
              <input
                type="radio"
                className="btn-check"
                name="eventView"
                id="monthly"
                value="monthly"
                checked={eventView === "monthly"}
                onChange={(e) => setEventView(e.target.value)}
              />
              <label className="btn btn-outline-primary" htmlFor="monthly">
                Monthly
              </label>

              <input
                type="radio"
                className="btn-check"
                name="eventView"
                id="yearly"
                value="yearly"
                checked={eventView === "yearly"}
                onChange={(e) => setEventView(e.target.value)}
              />
              <label className="btn btn-outline-primary" htmlFor="yearly">
                Yearly
              </label>
              {/* <label
                className={`btn ${
                  eventView === "yearly" ? "btn-primary" : "btn-outline-primary"
                }`}
                htmlFor="yearly"
              >
                Yearly
              </label> */}
            </div>
            <button
              className="btn btn-success btn-sm d-flex align-items-center gap-1"
              onClick={() =>
                onExport(
                  "CommitteeAdminEvents",
                  filters?.fromDate,
                  filters?.toDate,
                )
              }
            >
              <FaFileExcel size={14} />
              <span>Export to Excel</span>
            </button>
          </div>
        ),
        labels:
          eventView === "monthly"
            ? safeArray(monthlyEventsCountVisibleToCommitteeAdmin).length > 0
              ? monthlyEventsCountVisibleToCommitteeAdmin.map(
                  (x) => x.month || "",
                )
              : ["No Data"]
            : safeArray(yearlyEventsCountVisibleToCommitteeAdmin).length > 0
              ? yearlyEventsCountVisibleToCommitteeAdmin.map((x) =>
                  String(x.year),
                )
              : ["No Data"],

        series: [
          {
            name: "Events",
            data:
              eventView === "monthly"
                ? safeArray(monthlyEventsCountVisibleToCommitteeAdmin).length >
                  0
                  ? monthlyEventsCountVisibleToCommitteeAdmin.map(
                      (x) => Number(x.count) || 0,
                    )
                  : [0]
                : safeArray(yearlyEventsCountVisibleToCommitteeAdmin).length > 0
                  ? yearlyEventsCountVisibleToCommitteeAdmin.map(
                      (x) => Number(x.count) || 0,
                    )
                  : [0],
          },
        ],
        colors: ["#007bff"],
        exportEnabled: true,
      },
    ],
    [
      loginStatusVisibleToCommitteeAdmin,
      monthlyEventsCountVisibleToCommitteeAdmin,
      yearlyEventsCountVisibleToCommitteeAdmin,
      eventView,
    ],
  );

  // Function to update filters only
  const handleDateChange = (name, value) => {
    let updated = { ...filters, [name]: value };

    // keep range valid
    if (updated.fromDate && updated.toDate) {
      const from = new Date(updated.fromDate + "-01");
      const to = new Date(updated.toDate + "-01");
      if (from > to) {
        if (name === "fromDate") updated.toDate = updated.fromDate;
        else updated.fromDate = updated.toDate;
      }
    }

    setFilters(updated);
  };

  // Choose charts based on selected value
  const chartsToRender =
    selectedCommittee === "all" ? allCharts : singleCommitteeCharts;

  const toLocalMonthStart = (yyyyMm) => {
    const [year, month] = yyyyMm.split("-").map(Number);
    return new Date(year, month - 1, 1);
  };

  if (loading) return <CustomLoader />;

  return (
    <div>
      {/* ✅ Dropdown outside Dashboard */}
      <div className="flex justify-end mb-1">
        <div className="row align-items-end mb-1 p-4">
          <div className="col-md-3">
            <MonthYearPicker
              name="fromDate"
              label="From Date"
              value={filters.fromDate}
              handleChange={(val) => handleDateChange("fromDate", val)}
              errorMessage={errors}
              minDate={new Date("2014-01-01")}
              // maxDate={new Date(filters.toDate + "-01")}
              maxDate={toLocalMonthStart(filters.toDate)}
            />
          </div>

          <div className="col-md-3">
            <MonthYearPicker
              name="toDate"
              label="To Date"
              value={filters.toDate}
              handleChange={(val) => handleDateChange("toDate", val)}
              errorMessage={errors}
              // minDate={new Date(filters.fromDate + "-01")}
              minDate={toLocalMonthStart(filters.fromDate)}
              // maxDate={new Date("2030-11-01")}
              maxDate={new Date()}
            />
          </div>
          {console.log("selectedCommittee", selectedCommittee)}
          {userRoleID === 3 && (
            <div className="col-md-4">
              <SelectInput
                name="committees"
                label="Committee"
                options={committeeOptions}
                value={selectedCommittee}
                handleChange={(option) =>
                  handleSelectChange(option, "committees")
                }
                isMulti={false}
                isClearable={selectedCommittee === "all" ? false : true}
              />
            </div>
          )}
          <div className="col-md-2 d-flex align-items-end h-100 mt-2 mt-lg-0">
            <Button status="secondary" label="Search" onClick={handleSearch} />
          </div>
        </div>
      </div>

      {/* ✅ Dashboard always receives processed stats + charts */}
      <Dashboard stats={stats} charts={chartsToRender} />
    </div>
  );
};

export default AdminDashboard;
